import React, { useEffect, useState, useMemo } from "react";
import { Navigate } from "react-router-dom";
import {
  Loader2, DollarSign, Activity, Gauge, TrendingUp, Layers,
  Cpu, Users2, AlertTriangle, Calendar, Download,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const WINDOWS = [
  { v: "7", label: "Last 7 days" },
  { v: "30", label: "Last 30 days" },
  { v: "90", label: "Last 90 days" },
];

const usd = (n) => {
  if (n == null) return "—";
  const v = Number(n);
  if (v === 0) return "$0.00";
  if (Math.abs(v) < 0.01) return `$${v.toFixed(4)}`;
  return `$${v.toFixed(2)}`;
};
const intf = (n) => (n == null ? "—" : Number(n).toLocaleString());
const msFmt = (n) => (n == null ? "—" : `${(Number(n) / 1000).toFixed(1)}s`);

export default function AdminAnalytics() {
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [ctaData, setCtaData] = useState(null);
  const [downloads, setDownloads] = useState(null);
  const [busy, setBusy] = useState(false);
  const [days, setDays] = useState("30");

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    let alive = true;
    (async () => {
      setBusy(true);
      try {
        const [u, c, d] = await Promise.allSettled([
          api.get(`/admin/analytics/usage?days=${days}`),
          api.get(`/admin/analytics/upgrade-cta?days=${days}`),
          api.get(`/admin/analytics/brief-downloads?days=${days}`),
        ]);
        if (!alive) return;
        if (u.status === "fulfilled") setData(u.value.data);
        else throw u.reason;
        if (c.status === "fulfilled") setCtaData(c.value.data);
        if (d.status === "fulfilled") setDownloads(d.value.data);
      } catch (err) {
        toast.error("Couldn't load analytics", { description: formatApiError(err) });
      } finally {
        if (alive) setBusy(false);
      }
    })();
    return () => { alive = false; };
  }, [user, days]);

  const series = useMemo(() => data?.daily_series || [], [data]);
  const maxCost = useMemo(() => {
    if (!series.length) return 1;
    return Math.max(
      0.0001,
      ...series.map((d) => (Number(d.paid_cost) || 0) + (Number(d.free_cost) || 0)),
    );
  }, [series]);

  if (loading || user === null) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
      </div>
    );
  }
  if (!user || user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  const t = data?.totals || {};

  return (
    <div className="space-y-8" data-testid="admin-analytics-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] font-semibold text-blue-600">Admin</p>
          <h1 className="mt-1.5 font-display text-3xl font-semibold tracking-tight text-slate-900">
            AI usage &amp; unit economics
          </h1>
          <p className="mt-1.5 text-sm text-slate-600 max-w-2xl">
            Per-scan cost observability. Use this to verify margins before turning on
            paid acquisition or relaxing free-tier limits.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-slate-400" />
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="h-9 w-[160px]" data-testid="analytics-window">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WINDOWS.map((w) => (
                <SelectItem key={w.v} value={w.v} data-testid={`window-${w.v}`}>
                  {w.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat
          icon={Activity}
          label="Total scans"
          value={intf(t.total_scans)}
          sub={`${intf(t.paid_scans)} paid · ${intf(t.free_scans)} free`}
          testId="stat-total-scans"
        />
        <Stat
          icon={DollarSign}
          label="True AI cost"
          value={usd(t.true_total_ai_cost_usd ?? t.total_cost_usd)}
          sub={`Incl. chat · redline · compare`}
          testId="stat-true-cost"
          accent="emerald"
        />
        <Stat
          icon={TrendingUp}
          label="Projected monthly"
          value={usd(t.projected_monthly_true_cost_usd ?? t.projected_monthly_cost_usd)}
          sub={`Full-surface run-rate over ${data?.window_days || days}d`}
          testId="stat-projected"
          accent="amber"
        />
        <Stat
          icon={Gauge}
          label="Avg scan duration"
          value={msFmt(t.avg_processing_ms)}
          sub={`${intf((t.input_tokens || 0) + (t.output_tokens || 0))} scan tokens`}
          testId="stat-duration"
        />
      </div>

      {/* Failed scans warning */}
      {t.failed_scans > 0 && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
             data-testid="failed-scans-banner">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <span className="font-semibold">{t.failed_scans}</span> scan(s) failed in
            this window. Failed scans may still incur partial AI cost if the LLM call
            began before the failure.
          </div>
        </div>
      )}

      {/* Plan profitability */}
      <Card title="Plan profitability" icon={DollarSign}
            subtitle="Revenue − Paddle fees (5%) − true AI cost across scans, chat, redline & compare. Annual plans are normalised to monthly revenue.">
        {(!data?.plan_profitability || data.plan_profitability.length === 0) ? (
          <Empty label="No plan data yet." />
        ) : (
          <div className="overflow-x-auto -mx-2">
            <table className="min-w-full text-sm" data-testid="plan-profitability-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b">
                  <th className="px-2 py-2">Plan</th>
                  <th className="px-2 py-2 text-right">Workspaces</th>
                  <th className="px-2 py-2 text-right">Activity</th>
                  <th className="px-2 py-2 text-right">Revenue</th>
                  <th className="px-2 py-2 text-right">Paddle fees</th>
                  <th className="px-2 py-2 text-right">AI cost</th>
                  <th className="px-2 py-2 text-right">Gross margin</th>
                  <th className="px-2 py-2 text-right">Margin %</th>
                </tr>
              </thead>
              <tbody>
                {data.plan_profitability.map((p) => {
                  const tone = p.plan === "free"
                    ? "bg-slate-50"
                    : p.plan === "pro"
                    ? "bg-blue-50/40"
                    : "bg-emerald-50/40";
                  const marginColor = p.gross_margin_usd >= 0
                    ? "text-emerald-700" : "text-rose-700";
                  return (
                    <tr key={p.plan} className={`border-b last:border-0 ${tone}`}
                        data-testid={`plan-row-${p.plan}`}>
                      <td className="px-2 py-2.5 font-semibold capitalize">{p.plan}</td>
                      <td className="px-2 py-2.5 text-right tabular-nums">{intf(p.workspaces)}</td>
                      <td className="px-2 py-2.5 text-right text-xs text-slate-600 tabular-nums">
                        {intf(p.scans)} scans
                        {p.chats > 0 && <> · {intf(p.chats)} chats</>}
                        {p.redlines > 0 && <> · {intf(p.redlines)} redlines</>}
                        {p.compares > 0 && <> · {intf(p.compares)} cmp</>}
                      </td>
                      <td className="px-2 py-2.5 text-right tabular-nums">{usd(p.revenue_usd)}</td>
                      <td className="px-2 py-2.5 text-right tabular-nums text-slate-500">−{usd(p.paddle_fees_usd)}</td>
                      <td className="px-2 py-2.5 text-right tabular-nums text-slate-500">−{usd(p.ai_cost_usd)}</td>
                      <td className={`px-2 py-2.5 text-right tabular-nums font-semibold ${marginColor}`}>
                        {usd(p.gross_margin_usd)}
                      </td>
                      <td className={`px-2 py-2.5 text-right tabular-nums font-semibold ${marginColor}`}>
                        {p.gross_margin_pct == null ? "—" : `${p.gross_margin_pct}%`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="mt-3 text-[11px] text-slate-500 leading-relaxed px-2">
              Free plan margin is intentionally negative (acquisition cost).
              Break-even = ~1 Pro upgrade per ~85 free workspaces per month.
            </div>
          </div>
        )}
      </Card>

      {/* Acquisition brief downloads — surfaces who opened the gated
          PDF/MD link. Real-time buyer-intent signal for M&A and bizdev
          outreach. Renders only when there's at least one download. */}
      {downloads?.count > 0 && (
        <Card title="Acquisition brief downloads" icon={Download}
              subtitle={`${downloads.count} download${downloads.count === 1 ? "" : "s"} in the last ${data?.window_days || days} days. Each row is a real-time signal that someone opened the password-gated brief — follow up the same day for warm intent.`}>
          <div className="overflow-x-auto -mx-2" data-testid="brief-downloads-list">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b">
                  <th className="px-2 py-2">When</th>
                  <th className="px-2 py-2">Key</th>
                  <th className="px-2 py-2">Format</th>
                  <th className="px-2 py-2">IP</th>
                  <th className="px-2 py-2">User agent</th>
                </tr>
              </thead>
              <tbody>
                {downloads.items.slice(0, 20).map((d, idx) => {
                  // Friendly relative time — "3 minutes ago" beats raw ISO strings.
                  const ts = new Date(d.ts);
                  const ageMs = Date.now() - ts.getTime();
                  const rel = ageMs < 60_000 ? "just now"
                    : ageMs < 3600_000 ? `${Math.floor(ageMs / 60_000)}m ago`
                    : ageMs < 86_400_000 ? `${Math.floor(ageMs / 3600_000)}h ago`
                    : `${Math.floor(ageMs / 86_400_000)}d ago`;
                  // Strip "/Mozilla 5.0" noise — keep just the meaningful client hint.
                  const ua = (d.user_agent || "").match(/(Chrome|Safari|Firefox|Edge|curl|wget)[/\d.]*/i)?.[0]
                    || (d.user_agent ? d.user_agent.slice(0, 40) : "—");
                  return (
                    <tr key={idx} className="border-b last:border-0">
                      <td className="px-2 py-2.5 text-slate-700 tabular-nums">
                        {rel}
                        <span className="text-slate-400 ml-1.5 text-[11px]">
                          ({ts.toISOString().slice(0, 16).replace("T", " ")})
                        </span>
                      </td>
                      <td className="px-2 py-2.5">
                        {d.key ? (
                          <span className="inline-flex items-center rounded-md bg-blue-50 ring-1 ring-inset ring-blue-200 text-blue-800 px-2 py-0.5 text-[11px] font-mono">
                            {d.key}
                          </span>
                        ) : (
                          <span className="text-slate-400 italic">none</span>
                        )}
                      </td>
                      <td className="px-2 py-2.5 uppercase text-xs font-semibold text-slate-600">{d.format}</td>
                      <td className="px-2 py-2.5 tabular-nums text-slate-600 text-xs">{d.ip || "—"}</td>
                      <td className="px-2 py-2.5 text-slate-600 text-xs">{ua}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {downloads.items.length > 20 && (
              <div className="px-2 py-2 text-[11px] text-slate-500">
                Showing 20 of {downloads.items.length}. Older entries available via{" "}
                <code className="bg-slate-100 px-1 rounded">/api/admin/analytics/brief-downloads</code>.
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Upgrade-CTA conversion — clicks on the feature_locked toast's
          "Upgrade now" action. The bigger the per-feature count, the
          higher the buyer intent on that gated capability. */}
      {ctaData?.by_feature?.length > 0 && (
        <Card title="Upgrade CTA clicks" icon={TrendingUp}
              subtitle={`${ctaData.total_clicks} click${ctaData.total_clicks === 1 ? "" : "s"} on the "Upgrade now" button in feature-locked toasts. The features with the most clicks are the strongest upgrade levers.`}>
          {/* Top stat-row: 7-day conversion rate */}
          {ctaData?.conversion_7d?.clickers > 0 && (
            <div className="mb-5 grid grid-cols-3 gap-3" data-testid="cta-conversion-row">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 px-4 py-3">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-emerald-700">7-day conversion</div>
                <div className="mt-1 text-2xl font-bold tabular-nums text-emerald-900" data-testid="cta-conversion-pct">
                  {ctaData.conversion_7d.conversion_rate_pct == null
                    ? "—"
                    : `${ctaData.conversion_7d.conversion_rate_pct}%`}
                </div>
                <div className="text-[11px] text-emerald-700/80 mt-0.5 tabular-nums">
                  {ctaData.conversion_7d.converted_to_paid}/{ctaData.conversion_7d.clickers} upgraded
                </div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">CTA clickers (7d)</div>
                <div className="mt-1 text-2xl font-bold tabular-nums text-slate-900">{intf(ctaData.conversion_7d.clickers)}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">unique users</div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Window</div>
                <div className="mt-1 text-2xl font-bold tabular-nums text-slate-900">{intf(ctaData.total_clicks)}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">total clicks · {data?.window_days || days}d</div>
              </div>
            </div>
          )}

          {/* Time-series — same visual language as the AI-cost chart below */}
          {ctaData?.daily_series?.length > 0 && (() => {
            const max = Math.max(1, ...ctaData.daily_series.map((d) => d.clicks || 0));
            return (
              <div className="mb-4">
                <div className="flex items-end gap-1 h-24 px-1" data-testid="cta-daily-chart">
                  {ctaData.daily_series.map((d) => {
                    const h = Math.max(2, Math.round(((d.clicks || 0) / max) * 100));
                    return (
                      <div key={d.day}
                           className="flex-1 h-full flex flex-col-reverse items-stretch group relative">
                        <div className={`rounded-sm transition-all ${
                          d.clicks > 0 ? "bg-blue-600" : "bg-slate-100"
                        }`} style={{ height: `${h}%` }} />
                        <div className="absolute -top-9 left-1/2 -translate-x-1/2 hidden group-hover:block z-10 whitespace-nowrap rounded-md bg-slate-900 text-white text-[11px] px-2 py-1 shadow-lg">
                          {d.day}: {d.clicks} click{d.clicks === 1 ? "" : "s"}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-2 flex justify-between text-[10px] text-slate-400 tabular-nums px-1">
                  <span>{ctaData.daily_series[0]?.day}</span>
                  <span>{ctaData.daily_series[ctaData.daily_series.length - 1]?.day}</span>
                </div>
              </div>
            );
          })()}

          {/* Per-feature breakdown table — unchanged */}
          <div className="overflow-x-auto -mx-2">
            <table className="min-w-full text-sm" data-testid="cta-clicks-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b">
                  <th className="px-2 py-2">Feature</th>
                  <th className="px-2 py-2 text-right">Clicks</th>
                  <th className="px-2 py-2 text-right">Unique users</th>
                  <th className="px-2 py-2 text-right">Free</th>
                  <th className="px-2 py-2 text-right">Pro</th>
                </tr>
              </thead>
              <tbody>
                {ctaData.by_feature.map((f) => (
                  <tr key={f.feature} className="border-b last:border-0"
                      data-testid={`cta-row-${f.feature}`}>
                    <td className="px-2 py-2.5 font-medium capitalize">
                      {(f.feature || "(unknown)").replace(/_/g, " ")}
                    </td>
                    <td className="px-2 py-2.5 text-right tabular-nums font-semibold">{intf(f.total)}</td>
                    <td className="px-2 py-2.5 text-right tabular-nums text-slate-600">{intf(f.unique_users)}</td>
                    <td className="px-2 py-2.5 text-right tabular-nums text-slate-500">{intf(f.by_plan?.free || 0)}</td>
                    <td className="px-2 py-2.5 text-right tabular-nums text-slate-500">{intf(f.by_plan?.pro || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* AI surfaces breakdown */}
      <Card title="AI surfaces" icon={Layers}
            subtitle="Every place we burn LLM tokens. Surface omitted from this view = surface that's not paying its way.">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="ai-surfaces-grid">
          {Object.entries(data?.ai_surfaces || {}).map(([k, v]) => (
            <div key={k} className="rounded-lg border border-slate-200 bg-white px-3 py-2.5"
                 data-testid={`surface-${k}`}>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                {k.replace(/_/g, " ")}
              </div>
              <div className="text-lg font-bold tabular-nums mt-0.5">{usd(v.cost_usd)}</div>
              <div className="text-[11px] text-slate-500">{intf(v.count)} calls</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Cost alerts */}
      {data?.cost_alerts?.items?.length > 0 && (
        <Card title="Cost alerts" icon={AlertTriangle}
              subtitle={`Workspaces burning ≥ ${usd(data.cost_alerts.threshold_usd)} of AI cost this window. Power users (upsell candidates) or abuse (throttle candidates).`}>
          <div className="space-y-2" data-testid="cost-alerts-list">
            {data.cost_alerts.items.map((a) => (
              <div key={a.workspace_id}
                   className="flex items-center justify-between rounded-lg border border-rose-200 bg-rose-50/50 px-3 py-2 text-sm"
                   data-testid={`cost-alert-${a.workspace_id}`}>
                <div className="min-w-0">
                  <div className="font-medium text-slate-900 truncate">{a.workspace_name}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    plan={a.plan} · {a.scans} scans
                    {a.chats > 0 && <> · {a.chats} chats</>}
                    {a.redlines > 0 && <> · {a.redlines} redlines</>}
                    {a.compares > 0 && <> · {a.compares} compares</>}
                  </div>
                </div>
                <div className="text-right shrink-0 ml-3">
                  <div className="text-lg font-bold text-rose-700 tabular-nums">
                    {usd(a.total_cost_usd)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Free vs Paid */}
      <Card title="Free vs paid usage" icon={Layers}
            subtitle="Free = public landing-page scans. Paid = authenticated workspace scans.">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <UsageBar
            label="Free tier (public landing)"
            scans={data?.free_vs_paid?.free?.scans || 0}
            cost={data?.free_vs_paid?.free?.cost_usd || 0}
            totalScans={t.total_scans || 1}
            color="bg-amber-500"
            testId="usage-free"
          />
          <UsageBar
            label="Authenticated workspaces"
            scans={data?.free_vs_paid?.paid?.scans || 0}
            cost={data?.free_vs_paid?.paid?.cost_usd || 0}
            totalScans={t.total_scans || 1}
            color="bg-blue-600"
            testId="usage-paid"
          />
        </div>
      </Card>

      {/* Daily series mini-chart */}
      <Card title={`Daily AI cost (last ${data?.window_days || days} days)`} icon={TrendingUp}>
        {series.length === 0 ? (
          <Empty label="No scans yet in this window." />
        ) : (
          <div className="flex items-end gap-1 h-40 px-1" data-testid="daily-cost-chart">
            {series.map((d) => {
              const total = (Number(d.paid_cost) || 0) + (Number(d.free_cost) || 0);
              const h = Math.max(2, Math.round((total / maxCost) * 100));
              const paidH = total > 0 ? Math.round(((d.paid_cost || 0) / total) * h) : 0;
              const freeH = h - paidH;
              return (
                <div key={d.day}
                     className="flex-1 h-full flex flex-col-reverse items-stretch group relative">
                  <div className="bg-blue-600 rounded-sm transition-all"
                       style={{ height: `${paidH}%` }} />
                  <div className="bg-amber-400 rounded-sm transition-all"
                       style={{ height: `${freeH}%` }} />
                  <div className="absolute -top-12 left-1/2 -translate-x-1/2 hidden group-hover:block z-10 whitespace-nowrap rounded-md bg-slate-900 text-white text-[11px] px-2 py-1 shadow-lg">
                    {d.day}: {usd(total)} ({(d.paid_scans || 0) + (d.free_scans || 0)} scans)
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div className="mt-3 flex items-center gap-4 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-sm bg-blue-600" /> Paid
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-sm bg-amber-400" /> Free
          </span>
        </div>
      </Card>

      {/* Two-column: top expensive + by workspace */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Highest-cost scans" icon={DollarSign}>
          {(!data?.top_expensive_scans || data.top_expensive_scans.length === 0) ? (
            <Empty label="No completed paid scans yet." />
          ) : (
            <div className="overflow-x-auto -mx-5">
              <table className="min-w-full text-sm" data-testid="top-expensive-table">
                <thead className="text-left text-xs uppercase tracking-[0.12em] text-slate-500 font-semibold border-b border-slate-100">
                  <tr>
                    <th className="px-5 py-2.5">Document</th>
                    <th className="px-5 py-2.5 text-right">Tokens</th>
                    <th className="px-5 py-2.5 text-right">Cost</th>
                    <th className="px-5 py-2.5 text-right">Duration</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.top_expensive_scans.map((s) => (
                    <tr key={s.id} className="hover:bg-slate-50">
                      <td className="px-5 py-3 max-w-[220px]">
                        <div className="truncate font-medium text-slate-900">{s.document_name}</div>
                        <div className="text-xs text-slate-500">
                          {s.model || "—"} · {Math.round((s.file_size_chars || 0) / 1024)}KB chars
                        </div>
                      </td>
                      <td className="px-5 py-3 text-right tabular-nums text-slate-700">
                        {intf((s.input_tokens || 0) + (s.output_tokens || 0))}
                      </td>
                      <td className="px-5 py-3 text-right tabular-nums font-semibold text-slate-900">
                        {usd(s.cost_usd)}
                      </td>
                      <td className="px-5 py-3 text-right tabular-nums text-slate-700">
                        {msFmt(s.processing_ms)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card title="Scans by workspace" icon={Users2}>
          {(!data?.by_workspace || data.by_workspace.length === 0) ? (
            <Empty label="No workspace usage yet." />
          ) : (
            <div className="overflow-x-auto -mx-5">
              <table className="min-w-full text-sm" data-testid="by-workspace-table">
                <thead className="text-left text-xs uppercase tracking-[0.12em] text-slate-500 font-semibold border-b border-slate-100">
                  <tr>
                    <th className="px-5 py-2.5">Workspace</th>
                    <th className="px-5 py-2.5">Plan</th>
                    <th className="px-5 py-2.5 text-right">Scans</th>
                    <th className="px-5 py-2.5 text-right">Cost</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.by_workspace.map((w) => (
                    <tr key={w.workspace_id} className="hover:bg-slate-50">
                      <td className="px-5 py-3 font-medium text-slate-900 max-w-[200px]">
                        <div className="truncate">{w.workspace_name}</div>
                        <div className="text-xs text-slate-500 font-mono truncate">
                          {w.workspace_id}
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <span className="inline-flex rounded-full bg-slate-100 text-slate-700 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5">
                          {w.plan}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-right tabular-nums">{intf(w.scans)}</td>
                      <td className="px-5 py-3 text-right tabular-nums font-semibold">
                        {usd(w.cost_usd)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {/* By model */}
      <Card title="Cost by model" icon={Cpu}>
        {(!data?.by_model || data.by_model.length === 0) ? (
          <Empty label="No model usage recorded." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
               data-testid="by-model-grid">
            {data.by_model.map((m) => (
              <div key={m.model}
                   className="rounded-xl border border-slate-200 px-4 py-3 bg-slate-50/50">
                <div className="text-xs text-slate-500 font-mono truncate">{m.model}</div>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="font-display text-2xl font-semibold tabular-nums text-slate-900">
                    {usd(m.cost_usd)}
                  </span>
                  <span className="text-xs text-slate-500">/ {intf(m.scans)} scans</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <p className="text-xs text-slate-400 pt-2" data-testid="analytics-footnote">
        Token counts marked "estimate" use a character-count heuristic when the upstream
        wrapper doesn't return real usage. Pricing rates are configured per-model in
        <code className="mx-1">llm_provider.py</code>; override via
        <code className="mx-1">LLM_PRICE_INPUT_PER_M</code> /
        <code className="mx-1">LLM_PRICE_OUTPUT_PER_M</code>.
      </p>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub, testId, accent }) {
  const accentClass =
    accent === "emerald" ? "text-emerald-600" :
    accent === "amber" ? "text-amber-600" :
    "text-slate-900";
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
         data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500">
          {label}
        </div>
        <Icon className="h-4 w-4 text-slate-400" />
      </div>
      <div className={`mt-2 font-display text-3xl font-semibold tabular-nums ${accentClass}`}>
        {value}
      </div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

function Card({ title, subtitle, icon: Icon, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-100">
        <div>
          <div className="flex items-center gap-2">
            {Icon && <Icon className="h-4 w-4 text-slate-500" />}
            <h2 className="font-display text-base font-semibold text-slate-900">{title}</h2>
          </div>
          {subtitle && <p className="mt-1 text-xs text-slate-500">{subtitle}</p>}
        </div>
      </div>
      <div className="px-5 py-5">{children}</div>
    </div>
  );
}

function UsageBar({ label, scans, cost, totalScans, color, testId }) {
  const pct = totalScans ? Math.round((scans / totalScans) * 100) : 0;
  return (
    <div data-testid={testId}>
      <div className="flex items-baseline justify-between">
        <div className="text-sm font-medium text-slate-900">{label}</div>
        <div className="text-xs text-slate-500 tabular-nums">{intf(scans)} scans · {usd(cost)}</div>
      </div>
      <div className="mt-2 h-2.5 w-full rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-1 text-[11px] text-slate-400 tabular-nums">{pct}% of total volume</div>
    </div>
  );
}

function Empty({ label }) {
  return <div className="py-8 text-center text-sm text-slate-500">{label}</div>;
}
